/**
 * Lightweight client-side logger mirroring the server logger API.
 *
 * The logger retains the most recent entries in an in-memory ring buffer with
 * a default retention size of 200 entries.
 */

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent'
export const LOG_LEVEL_STORAGE_KEY = 'LOG_LEVEL'
export const LOG_LEVELS: ReadonlyArray<LogLevel> = [
    'trace',
    'debug',
    'info',
    'warn',
    'error',
    'silent',
]
export type LoggerContextValue = string | number | boolean | null | undefined
export type LoggerContext = Record<string, LoggerContextValue>

const levelPriority: Record<LogLevel, number> = {
    trace: 10,
    debug: 20,
    info: 30,
    warn: 40,
    error: 50,
    silent: 60,
}

type ActiveLogLevel = Exclude<LogLevel, 'silent'>

const consoleMethod: Record<ActiveLogLevel, (...args: unknown[]) => void> = {
    trace: console.trace.bind(console),
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
}

const isLogLevel = (value: string): value is LogLevel => value in levelPriority

const resolveLogLevel = (value?: string | null): LogLevel => {
    if (!value) return 'info'
    const normalized = value.trim().toLowerCase()
    return isLogLevel(normalized) ? normalized : 'info'
}

const readProcessLogLevel = (): string | undefined => {
    if (typeof process === 'undefined') return undefined
    const env = (process as unknown as { env?: Record<string, string | undefined> }).env
    return env?.LOG_LEVEL
}

const readStorageLogLevel = (): string | undefined => {
    if (typeof window === 'undefined' || !('localStorage' in window)) return undefined
    try {
        return window.localStorage.getItem(LOG_LEVEL_STORAGE_KEY) ?? undefined
    } catch (_error) {
        return undefined
    }
}

const readSearchLogLevel = (): string | undefined => {
    if (typeof window === 'undefined') return undefined
    try {
        const search = new URLSearchParams(window.location.search)
        return (
            search.get('logLevel') ||
            search.get('log_level') ||
            search.get('LOG_LEVEL') ||
            undefined
        )
    } catch (_error) {
        return undefined
    }
}

let currentLevel: LogLevel = resolveLogLevel(
    readSearchLogLevel() ?? readStorageLogLevel() ?? readProcessLogLevel()
)

type LogLevelSubscriber = (level: LogLevel) => void

const subscribers = new Set<LogLevelSubscriber>()

const notifySubscribers = (): void => {
    for (const subscriber of subscribers) {
        try {
            subscriber(currentLevel)
        } catch (_error) {
            // Ignore subscriber errors to avoid cascading failures.
        }
    }
}

const getLogLevel = (): LogLevel => currentLevel
const persistLogLevel = (level: LogLevel): void => {
    if (typeof window === 'undefined' || !('localStorage' in window)) return
    try {
        window.localStorage.setItem(LOG_LEVEL_STORAGE_KEY, level)
    } catch (_error) {
        // Ignore persistence errors (storage might be unavailable, such as in private mode).
    }
}

const setLogLevel = (next: LogLevel | string): void => {
    const resolved = typeof next === 'string' ? resolveLogLevel(next) : next
    if (currentLevel === resolved) return

    currentLevel = resolved
    persistLogLevel(currentLevel)
    notifySubscribers()
}

const subscribeToLogLevel = (listener: LogLevelSubscriber): (() => void) => {
    subscribers.add(listener)
    return () => {
        subscribers.delete(listener)
    }
}

const shouldLog = (level: ActiveLogLevel): boolean =>
    levelPriority[level] >= levelPriority[currentLevel]

const pad2 = (value: number) => (value < 10 ? `0${value}` : String(value))
const pad3 = (value: number) =>
    value < 10 ? `00${value}` : value < 100 ? `0${value}` : String(value)

const formatTimestamp = (): string => {
    const date = new Date()
    const hour = pad2(date.getHours())
    const minute = pad2(date.getMinutes())
    const second = pad2(date.getSeconds())
    const milli = pad3(date.getMilliseconds())
    return `[${hour}:${minute}:${second}.${milli}]`
}

const formatContext = (context: LoggerContext): { scopeTag: string; rest: string } => {
    const entries = Object.entries(context).filter(
        ([, value]) => value !== undefined && value !== null && value !== ''
    )

    const scopeEntry = entries.find(([key]) => key === 'scope')
    const scopeText = scopeEntry ? String(scopeEntry[1]) : ''
    const scopeTag = scopeText ? `[${scopeText}]` : ''

    const rest = entries
        .filter(([key]) => key !== 'scope')
        .map(([key, value]) => `${key}=${String(value)}`)
        .join(' ')

    return { scopeTag, rest }
}

export interface Logger {
    readonly level: () => LogLevel
    readonly setLevel: (level: LogLevel | string) => void
    readonly subscribe: (listener: LogLevelSubscriber) => () => void
    readonly isEnabled: (level: LogLevel) => boolean
    readonly trace: (...args: unknown[]) => void
    readonly debug: (...args: unknown[]) => void
    readonly info: (...args: unknown[]) => void
    readonly warn: (...args: unknown[]) => void
    readonly error: (...args: unknown[]) => void
    readonly withContext: (context: LoggerContext) => Logger
}

export interface LogEntry {
    readonly timestamp: string
    readonly level: ActiveLogLevel
    readonly context: LoggerContext
    readonly argumentsList: unknown[]
}

type LogEntryListener = (entry: LogEntry) => void

const DEFAULT_LOG_HISTORY_LIMIT = 200

const history: LogEntry[] = []
const historyListeners = new Set<LogEntryListener>()

const pushHistory = (entry: LogEntry): void => {
    history.push(entry)
    if (history.length > DEFAULT_LOG_HISTORY_LIMIT) {
        history.shift()
    }

    for (const listener of historyListeners) {
        try {
            listener(entry)
        } catch (_error) {
            // Ignore listener errors to avoid cascading failures.
        }
    }
}

const getHistory = (): ReadonlyArray<LogEntry> => history.slice()

const clearHistory = (): void => {
    history.length = 0
}

const onEntry = (listener: LogEntryListener): (() => void) => {
    historyListeners.add(listener)
    return () => {
        historyListeners.delete(listener)
    }
}

const createLogger = (baseContext: LoggerContext = {}): Logger => {
    const canLog = (level: LogLevel): boolean => {
        if (level === 'silent') return false
        return shouldLog(level as ActiveLogLevel)
    }

    const log = (level: ActiveLogLevel, ...args: unknown[]): void => {
        if (!canLog(level)) return

        const timestamp = formatTimestamp()
        const { scopeTag, rest } = formatContext(baseContext)
        const levelText = level.toUpperCase()
        const head = [timestamp, scopeTag, levelText, rest].filter(Boolean).join(' ')

        pushHistory({
            timestamp,
            level,
            context: { ...baseContext },
            argumentsList: args,
        })

        consoleMethod[level](head, ...args)
    }

    return {
        level: getLogLevel,
        setLevel: setLogLevel,
        subscribe: subscribeToLogLevel,
        isEnabled: (level: LogLevel) => canLog(level),
        trace: (...argumentsList: unknown[]) => log('trace', ...argumentsList),
        debug: (...argumentsList: unknown[]) => log('debug', ...argumentsList),
        info: (...argumentsList: unknown[]) => log('info', ...argumentsList),
        warn: (...argumentsList: unknown[]) => log('warn', ...argumentsList),
        error: (...argumentsList: unknown[]) => log('error', ...argumentsList),
        withContext: (context: LoggerContext) => createLogger({ ...baseContext, ...context }),
    }
}

const logger = createLogger()

export { createLogger, logger, getHistory, clearHistory, onEntry, DEFAULT_LOG_HISTORY_LIMIT }
